import { Food, FoodStatus } from '@cf-shared/data-model/food';
import { deepClone } from '@cf-shared/deep-helpers';

/**
 * The device-only `local` block to put on the copy the server hands back when publishing
 * FORKED a food (df-18a753fad787).
 *
 * Publishing a food this user does not own makes the server write a copy under a new id,
 * and the server clears `local` before every write because it must never reach the global
 * food database. The editor then adopts that copy in place of the one the user was editing.
 * Without this, the copy would lose everything the device kept about the food: whether a
 * recipe is shared, the like, and the last-used time and measure. A shared recipe would then
 * read as unshared, so its share menu is wrong and its later edits stay on the device.
 *
 * `hashSource` is left out on purpose. It is the author stamp that says "not published yet",
 * and the copy has just been published, so carrying it over would make the publish sweep send
 * the copy again and make a failed unpublish of it look harmless.
 */
export function localBlockForForkedCopy(editedFood: Food): Food['local'] {
  // if the edited food kept nothing on the device, there is nothing to carry over
  if (!editedFood.local) {
    return undefined;
  }
  const local = deepClone(editedFood.local);
  delete local.hashSource;
  return local;
}

/** What `adoptSharedRecipeCopyAsync` needs from the editor page, passed in so it runs under the node test runner. */
export interface SharedRecipeCopyDeps {
  /** write the copy into the user's database; a failure throws */
  saveCopyAsync(copy: Food): Promise<void>;
  /** remove the recipe the copy replaces from the user's database; reports its own failure */
  removeSupersededAsync(edited: Food): Promise<void>;
}

/**
 * When the first share of a recipe came back from the server as a copy under a NEW id
 * (df-0497c91194af), save that copy as the user's recipe in place of the one they shared, and
 * answer it. Answers `undefined` when the server kept the id, which is the ordinary case.
 *
 * The copy is saved BEFORE the old recipe is removed, so a save that fails leaves the user's
 * old recipe where it was rather than leaving them with neither. The failure is thrown for the
 * caller to report. Save on the edit page removes first and saves later; that order is older
 * than this and is left alone.
 */
export async function adoptSharedRecipeCopyAsync(
  edited: Food,
  published: Food | undefined,
  deps: SharedRecipeCopyDeps,
): Promise<Food | undefined> {
  // if the server kept the id, there is no copy to adopt
  if (!published || published._id === edited._id) {
    return undefined;
  }
  published.status = FoodStatus.Private;
  published.local = localBlockForForkedCopy(edited);
  await deps.saveCopyAsync(published);
  await deps.removeSupersededAsync(edited);
  return published;
}
